import React from 'react';
import { AutoScore, TraitScore } from '../types';

interface WritingResult {
  taskId: string;
  taskNumber: number;
  taskTitle: string;
  responseText: string;
  autoScore: AutoScore;
}

interface ScoreResultProps {
  results: WritingResult[];
  onRetry: () => void;
  onNext?: () => void;
  nextLabel?: string;
}

// CLB bands for PTE Core Writing based on the provided table
const getWritingCLB = (score: number): number => {
  if (score === 90) return 10;
  if (score >= 88 && score <= 89) return 9;
  if (score >= 79 && score <= 87) return 8;
  if (score >= 69 && score <= 78) return 7;
  if (score >= 60 && score <= 68) return 6;
  if (score >= 51 && score <= 59) return 5;
  if (score >= 41 && score <= 50) return 4;
  if (score >= 32 && score <= 40) return 3;
  return 3; // Below 32
};

const TraitBar: React.FC<{ label: string; data: TraitScore }> = ({ label, data }) => {
  const percentage = (data.score / data.maxScore) * 100;

  let colorClass = 'bg-green-500';
  if (percentage < 50) colorClass = 'bg-red-500';
  else if (percentage < 80) colorClass = 'bg-yellow-500';

  return (
    <div className="mb-3">
      <div className="flex justify-between items-end mb-1">
        <span className="font-medium text-gray-700 capitalize text-sm">{label.replace(/([A-Z])/g, ' $1').trim()}</span>
        <span className="text-sm font-bold text-gray-900">
          {data.score} / {data.maxScore}
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className={`h-2 rounded-full transition-all duration-500 ${colorClass}`}
          style={{ width: `${percentage}%` }}
        ></div>
      </div>
      {data.comments && (
        <p className="text-xs text-gray-500 italic mt-1">{data.comments}</p>
      )}
    </div>
  );
};

const ScoreResult: React.FC<ScoreResultProps> = ({ results, onRetry, onNext, nextLabel }) => {
  // Helper to normalize scoring based on task type
  const getTaskScoring = (result: WritingResult) => {
    const isSummarize =
      (result.taskTitle && result.taskTitle.toLowerCase().includes('summarize written text')) ||
      (!result.taskTitle && result.taskNumber === 1); // Fallback for Task 1

    if (isSummarize) {
      // Force max score of 2 for relevant traits
      const traits = ['content', 'form', 'grammar', 'vocabulary'] as const;
      let currentScore = 0;
      let currentMax = 0;

      traits.forEach(t => {
        // Ensure we don't exceed 2 if backend sends higher
        const s = Math.min(result.autoScore[t].score, 2);
        currentScore += s;
        currentMax += 2;
      });

      return {
        score: currentScore,
        maxScore: currentMax,
        isSummarize: true
      };
    }

    return {
      score: result.autoScore.overall.score,
      maxScore: result.autoScore.overall.maxScore,
      isSummarize: false
    };
  };

  // Calculate overall score out of 90 based on ADJUSTED scores
  const totalScore = results.reduce((sum, r) => sum + getTaskScoring(r).score, 0);
  const totalMaxScore = results.reduce((sum, r) => sum + getTaskScoring(r).maxScore, 0);

  // Guard against divide by zero
  const scoreOut90 = totalMaxScore > 0 ? Math.round((totalScore / totalMaxScore) * 90) : 0;
  const clbLevel = getWritingCLB(scoreOut90);

  return (
    <div className="ptest-root">
      {/* Overall Score Header */}
      <div className="bg-white rounded-xl shadow-lg p-8 mb-8 border border-gray-200">
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">Writing Test Results</h2>

        <div className="flex flex-wrap justify-center gap-8">
          {/* Score out of 90 */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 px-8 py-6 rounded-xl border border-blue-200 text-center">
            <span className="block text-sm text-blue-600 font-semibold uppercase tracking-wider mb-2">Overall Score</span>
            <span className="block text-5xl font-extrabold text-blue-900">
              {scoreOut90} <span className="text-2xl text-blue-400">/ 90</span>
            </span>
          </div>

          {/* CLB Band */}
          <div className="bg-gradient-to-br from-amber-50 to-amber-100 px-8 py-6 rounded-xl border border-amber-200 text-center">
            <span className="block text-sm text-amber-700 font-semibold uppercase tracking-wider mb-2">CLB Level</span>
            <span className="block text-5xl font-extrabold text-amber-900">
              {clbLevel}
            </span>
          </div>
        </div>

        {/* CLB Reference Table */}
        <div className="mt-8 overflow-x-auto">
          <table className="mx-auto text-sm border-collapse">
            <thead>
              <tr className="bg-amber-500 text-white">
                <th className="px-4 py-2 border border-amber-600">CLB</th>
                <th className="px-4 py-2 border border-amber-600">PTE Core Writing</th>
              </tr>
            </thead>
            <tbody>
              {[
                { clb: 10, range: '90' },
                { clb: 9, range: '88-89' },
                { clb: 8, range: '79-87' },
                { clb: 7, range: '69-78' },
                { clb: 6, range: '60-68' },
                { clb: 5, range: '51-59' },
                { clb: 4, range: '41-50' },
                { clb: 3, range: '32-40' },
              ].map((row) => (
                <tr
                  key={row.clb}
                  className={clbLevel === row.clb ? 'bg-amber-100 font-bold' : 'bg-white'}
                >
                  <td className="px-4 py-2 border border-gray-300 text-center">{row.clb}</td>
                  <td className="px-4 py-2 border border-gray-300 text-center">{row.range}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Individual Task Feedback */}
      <h3 className="text-xl font-bold text-gray-800 mb-4">Detailed Feedback by Question</h3>

      {results.map((result, index) => {
        const { score, maxScore, isSummarize } = getTaskScoring(result);

        return (
          <div key={result.taskId} className="bg-white rounded-xl shadow-md p-6 mb-6 border border-gray-200">
            <div className="flex justify-between items-center mb-4 pb-4 border-b">
              <h4 className="text-lg font-bold text-gray-800">
                Question {index + 1}: {result.taskTitle || `Task ${result.taskNumber}`}
              </h4>
              <div className="bg-blue-100 px-4 py-2 rounded-lg">
                <span className="font-bold text-blue-800">
                  {score} / {maxScore}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
              <TraitBar label="Content" data={isSummarize ? { ...result.autoScore.content, maxScore: 2, score: Math.min(result.autoScore.content.score, 2) } : result.autoScore.content} />
              <TraitBar label="Form" data={isSummarize ? { ...result.autoScore.form, maxScore: 2, score: Math.min(result.autoScore.form.score, 2) } : result.autoScore.form} />
              <TraitBar label="Grammar" data={isSummarize ? { ...result.autoScore.grammar, maxScore: 2, score: Math.min(result.autoScore.grammar.score, 2) } : result.autoScore.grammar} />
              <TraitBar label="Vocabulary" data={isSummarize ? { ...result.autoScore.vocabulary, maxScore: 2, score: Math.min(result.autoScore.vocabulary.score, 2) } : result.autoScore.vocabulary} />

              {/* For non-Summarize tasks, show remaining traits */}
              {!isSummarize && (
                <>
                  <TraitBar label="Spelling" data={result.autoScore.spelling} />
                  <TraitBar label="Organization" data={result.autoScore.organization} />
                  {result.autoScore.emailConventions && <TraitBar label="Email Conventions" data={result.autoScore.emailConventions} />}
                </>
              )}
            </div>

            {/* Show user's response */}
            {result.responseText && (
              <div className="mt-4 pt-4 border-t">
                <h5 className="font-semibold text-gray-700 mb-2">Your Response:</h5>
                <div className="bg-gray-50 p-4 rounded-lg text-gray-700 text-sm italic border">
                  {result.responseText || '(No response submitted)'}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Action Buttons */}
      <div className="ptest-header" style={{ marginTop: '20px' }}>
        <div></div>
        <div className="right-controls flex gap-4">
          <button
            onClick={onRetry}
            className="btn btn-primary"
          >
            Try Again
          </button>
          {onNext && (
            <button
              onClick={onNext}
              className="btn btn-primary bg-green-600 hover:bg-green-700 border-green-600"
            >
              {nextLabel || 'Next Section'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScoreResult;
